import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { CloseIcon } from '../icons';
import { IconButton } from './IconButton';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /**
   * `side` is the 400px docked panel on the web (Smolit); `bottom` is the
   * mobile sheet the brief calls for on filters and the map view.
   */
  placement?: 'side' | 'bottom';
  /** Rendered inside the sheet's header row, beside the close button. */
  header?: ReactNode;
  /** Tints the header the way Smolit's panel is tinted. */
  headerTone?: 'plain' | 'moana';
  children: ReactNode;
  /** Pinned to the bottom of the sheet — composer, sticky CTA. */
  footer?: ReactNode;
  label: string;
  className?: string;
}

/**
 * Anything that can take focus. `[tabindex="-1"]` is deliberately excluded:
 * such elements are focusable programmatically but are not Tab stops, so they
 * must not be part of the cycle.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.tabIndex !== -1,
  );
}

/**
 * The overlay surface behind Smolit on the web and behind filters and the map
 * on mobile.
 *
 * Because it declares `aria-modal`, it has to behave like one: while it is open
 * focus moves into it, Tab and Shift+Tab cycle within it rather than reaching
 * the obscured page behind, Escape closes it, page scroll is locked, and on
 * close focus returns to whatever opened it.
 */
export function Sheet({
  open,
  onClose,
  placement = 'side',
  header,
  headerTone = 'plain',
  children,
  footer,
  label,
  className,
}: SheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // The modal lifecycle effect below must depend on `open` alone. Callers pass
  // an inline arrow for onClose, so depending on it would tear the effect down
  // and set it up again on every parent render — restoring focus to the trigger
  // and then pulling it back to the sheet's first control. Anything controlled
  // inside the sheet, such as Smolit's composer, would lose focus on every
  // keystroke. Holding the latest callback in a ref keeps it current without
  // making it a dependency.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    const dialog = dialogRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;

    // Move focus in. The dialog itself is the fallback when it holds nothing
    // focusable, so focus never stays on the page behind.
    const initial = dialog ? focusableWithin(dialog)[0] : undefined;
    (initial ?? dialog)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !dialog) return;

      const items = focusableWithin(dialog);

      if (items.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      const outside = !dialog.contains(active);

      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      // Take focus back only if the sheet still had it. By the time this runs
      // React may already have detached the dialog, which drops focus to the
      // body — that counts as the sheet having had it. If something else has
      // deliberately claimed focus in the meantime, leave it alone.
      const active = document.activeElement;
      const sheetHadFocus =
        !active || active === document.body || (dialog?.contains(active) ?? false);

      if (sheetHadFocus) {
        previouslyFocused?.focus?.();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-60 flex',
        placement === 'side' ? 'justify-end' : 'items-end justify-center',
      )}
    >
      <div
        className="absolute inset-0 bg-[rgb(26_31_28_/_0.32)]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={cn(
          'relative flex flex-col bg-surface outline-none',
          placement === 'side'
            ? 'h-full w-[400px] max-w-[92vw] animate-sheet border-l border-line shadow-dock'
            : 'max-h-[88vh] w-full animate-sheet-up rounded-t-hero shadow-sheet pb-safe',
          className,
        )}
      >
        {placement === 'bottom' ? (
          <div className="flex justify-center pt-2.5 pb-1">
            <span className="h-1 w-9 rounded-full bg-line-firm" />
          </div>
        ) : null}

        {header ? (
          <div
            className={cn(
              'flex items-center gap-[11px] border-b border-line-soft px-[18px] py-4',
              headerTone === 'moana' && 'bg-gradient-to-b from-moana-tint to-surface',
            )}
          >
            <div className="flex flex-1 items-center gap-[11px]">{header}</div>
            <IconButton label="Close" size="sm" shape="square" onClick={onClose}>
              <CloseIcon size={16} />
            </IconButton>
          </div>
        ) : null}

        <div className="scrollbar-none flex-1 overflow-y-auto">{children}</div>

        {footer ? <div className="border-t border-line-soft px-3.5 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}
